import { Injectable } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { LiquidacionesStateService, LiquidacionesVdState } from './liquidaciones-state.service';
import { LoggerService } from '../core/services/logger.service';

@Injectable({
    providedIn: 'root'
})
export class LiquidacionesNavigationService {

    constructor(
        // eslint-disable-next-line no-unused-vars
        private router: Router,
        // eslint-disable-next-line no-unused-vars
        private stateService: LiquidacionesStateService,
        // eslint-disable-next-line no-unused-vars
        private logger: LoggerService
    ) {}

    /**
     * Navegar al detalle de liquidación VD manteniendo el estado.
     * @param id - ID de la liquidación
     * @param extraState - Estado adicional (ej: numeroLiquidacion) para pasar al detalle
     */
    navigateToVdDetail(id: number, extraState?: { numeroLiquidacion?: string }): void {
        const currentState = this.stateService.getCurrentVdState();
        const queryParams = this.stateService.stateToQueryParams(currentState);

        this.logger.info(
            `Navegando a detalle VD ${id} con estado conservado`,
            'LiquidacionesNavigationService'
        );

        this.router.navigate(['/liquidaciones-vd', id], {
            queryParams,
            state: extraState || undefined
        });
    }

    /**
     * Abre el detalle VD en una nueva pestaña. Replica filtros en query string y
     * pasa N° liquidación como `nl` (el detalle ya lo lee si no hay Router state).
     */
    openVdDetailInNewTab(
        id: number,
        extraState?: { numeroLiquidacion?: string }
    ): void {
        const currentState = this.stateService.getCurrentVdState();
        const queryParams: Record<string, string> = {
            ...this.stateService.stateToQueryParams(currentState),
        };
        if (extraState?.numeroLiquidacion) {
            queryParams['nl'] = extraState.numeroLiquidacion;
        }

        this.logger.info(
            `Abriendo detalle VD ${id} en nueva pestaña con estado conservado`,
            'LiquidacionesNavigationService'
        );

        const urlTree = this.router.createUrlTree(['/liquidaciones-vd', id], {
            queryParams,
        });
        const url = this.router.serializeUrl(urlTree);
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    /**
     * Navegar al detalle de liquidación Agencia manteniendo el estado
     */
    navigateToAgenciaDetail(id: number): void {
        const currentState = this.stateService.getCurrentAgenciaState();
        const queryParams = this.stateService.stateToQueryParams(currentState);

        this.logger.info(
            `Navegando a detalle Agencia ${id} con estado conservado`,
            'LiquidacionesNavigationService'
        );

        this.router.navigate(['/liquidaciones-agencia', id], {
            queryParams
        });
    }

    /**
     * Abre el detalle de liquidación Agencia en una nueva pestaña (mismos query params que navigateToAgenciaDetail).
     */
    openAgenciaDetailInNewTab(id: number): void {
        const currentState = this.stateService.getCurrentAgenciaState();
        const queryParams = this.stateService.stateToQueryParams(currentState);

        this.logger.info(
            `Abriendo detalle Agencia ${id} en nueva pestaña con estado conservado`,
            'LiquidacionesNavigationService'
        );

        const urlTree = this.router.createUrlTree(['/liquidaciones-agencia', id], {
            queryParams,
        });
        const url = this.router.serializeUrl(urlTree);
        window.open(url, '_blank', 'noopener,noreferrer');
    }

    /**
     * Regresar al listado de VD desde el detalle
     */
    backToVdList(): void {
        const currentState = this.stateService.getCurrentVdState();
        const queryParams = this.stateService.stateToQueryParams(currentState);

        this.logger.info(
            'Regresando al listado VD con estado conservado',
            'LiquidacionesNavigationService'
        );

        this.router.navigate(['/liquidaciones-vd'], {
            queryParams
        });
    }

    /**
     * Regresar al listado de Agencia desde el detalle
     */
    backToAgenciaList(): void {
        const currentState = this.stateService.getCurrentAgenciaState();
        const queryParams = this.stateService.stateToQueryParams(currentState);

        this.logger.info(
            'Regresando al listado Agencia con estado conservado',
            'LiquidacionesNavigationService'
        );

        this.router.navigate(['/liquidaciones-agencia'], {
            queryParams
        });
    }

    /**
     * Aplicar filtros y actualizar la URL para VD
     */
    applyVdFilters(): void {
        const currentState = this.stateService.getCurrentVdState();
        const queryParams = this.stateService.stateToQueryParams(currentState);

        this.logger.debug(
            `Aplicando filtros VD y actualizando URL: ${JSON.stringify(queryParams)}`,
            'LiquidacionesNavigationService'
        );

        this.router.navigate([], {
            relativeTo: this.getActivatedRoute(),
            queryParams,
            queryParamsHandling: 'replace' // Reemplazar completamente los params
        });
    }

    /**
     * Aplicar filtros y actualizar la URL para Agencia
     */
    applyAgenciaFilters(): void {
        const currentState = this.stateService.getCurrentAgenciaState();
        const queryParams = this.stateService.stateToQueryParams(currentState);

        this.logger.debug(
            `Aplicando filtros Agencia y actualizando URL: ${JSON.stringify(queryParams)}`,
            'LiquidacionesNavigationService'
        );

        this.router.navigate([], {
            relativeTo: this.getActivatedRoute(),
            queryParams,
            queryParamsHandling: 'replace' // Reemplazar completamente los params
        });
    }

    /**
     * Cargar estado desde query parameters para VD
     */
    loadVdStateFromUrl(route: ActivatedRoute): boolean {
        const queryParams = route.snapshot.queryParams;
        
        if (this.hasValidQueryParams(queryParams)) {
            const state = this.stateService.queryParamsToState(queryParams, true) as LiquidacionesVdState;
            
            this.logger.info(
                `Cargando estado VD desde URL: ${JSON.stringify(queryParams)}`,
                'LiquidacionesNavigationService'
            );

            // Actualizar el estado sin triggear cambios en la URL
            this.stateService.updateVdState(state);
            return true;
        }

        return false;
    }

    /**
     * Cargar estado desde query parameters para Agencia
     */
    loadAgenciaStateFromUrl(route: ActivatedRoute): boolean {
        const queryParams = route.snapshot.queryParams;
        
        if (this.hasValidQueryParams(queryParams)) {
            const state = this.stateService.queryParamsToState(queryParams, false);
            
            this.logger.info(
                `Cargando estado Agencia desde URL: ${JSON.stringify(queryParams)}`,
                'LiquidacionesNavigationService'
            );

            // Actualizar el estado sin triggear cambios en la URL
            this.stateService.updateAgenciaState(state);
            return true;
        }

        return false;
    }

    /**
     * Limpiar filtros y URL para VD
     */
    clearVdFilters(): void {
        this.stateService.clearVdState();
        
        this.logger.info(
            'Limpiando filtros VD y URL',
            'LiquidacionesNavigationService'
        );

        this.router.navigate([], {
            relativeTo: this.getActivatedRoute(),
            queryParams: {},
            queryParamsHandling: 'replace'
        });
    }

    /**
     * Limpiar filtros y URL para Agencia
     */
    clearAgenciaFilters(): void {
        this.stateService.clearAgenciaState();
        
        this.logger.info(
            'Limpiando filtros Agencia y URL',
            'LiquidacionesNavigationService'
        );

        this.router.navigate([], {
            relativeTo: this.getActivatedRoute(),
            queryParams: {},
            queryParamsHandling: 'replace'
        });
    }

    /**
     * Verificar si los query parameters contienen datos válidos
     */
    private hasValidQueryParams(params: any): boolean {
        const validKeys = [
            'clienteId', 'temporada', 'fechaInicio', 'fechaFin', 
            'filtroTexto', 'page', 'pageSize', 
            'filtroTipoEnvio', 'filtroCoberturaSeguro'
        ];

        return validKeys.some(key => Object.prototype.hasOwnProperty.call(params, key) && params[key] !== null && params[key] !== '');
    }

    /**
     * Obtener la ruta activada actual (helper)
     */
    private getActivatedRoute(): ActivatedRoute {
        let route = this.router.routerState.root;
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route;
    }

    /**
     * Obtener los query parameters actuales
     */
    getCurrentQueryParams(): any {
        return this.router.routerState.root.snapshot.queryParams;
    }

    /**
     * Verificar si estamos en una página de detalle
     */
    isDetailPage(): boolean {
        const url = this.router.url;
        return /\/liquidaciones-(vd|agencia)\/\d+/.test(url);
    }

    /**
     * Obtener el tipo de liquidación actual basado en la URL
     */
    getCurrentLiquidationType(): 'vd' | 'agencia' | null {
        const url = this.router.url;
        if (url.includes('/liquidaciones-vd')) return 'vd';
        if (url.includes('/liquidaciones-agencia')) return 'agencia';
        return null;
    }
}